import { studioAIService } from '@/@admin/services';
import { MessageMarkerStatus } from '@/@admin/types/entities';
import { tracking } from '@/lib/features/tracking';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

interface UseMessageMarkerMutationsProps {
    conversationId: string;
    messageId: string;
    userMessage?: string;
    aiResponse?: string;
    onSuccess?: () => void;
}

export function useMessageMarkerMutations({
    conversationId,
    messageId,
    userMessage,
    aiResponse,
    onSuccess,
}: UseMessageMarkerMutationsProps) {
    const queryClient = useQueryClient();

    // Create marker mutation
    const createMarkerMutation = useMutation({
        mutationFn: async ({ status, notes }: { status: MessageMarkerStatus; notes?: string }) => {
            return studioAIService.createMessageMarker({
                conversationId,
                messageId,
                status,
                notes,
                userMessage,
                aiResponse,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['message-markers'] });
            onSuccess?.();
        },
        onError: (error) => {
            tracking.errorWithSource(error as Error, 'useMessageMarkerMutations.createMarker');
        },
    });

    // Update marker mutation
    const updateMarkerMutation = useMutation({
        mutationFn: async ({
            markerId,
            status,
            notes,
        }: {
            markerId: number;
            status?: MessageMarkerStatus;
            notes?: string;
        }) => {
            return studioAIService.updateMessageMarker(markerId, {
                status,
                notes,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['message-markers'] });
            onSuccess?.();
        },
        onError: (error) => {
            tracking.errorWithSource(error as Error, 'useMessageMarkerMutations.updateMarker');
        },
    });

    // Delete marker mutation
    const deleteMarkerMutation = useMutation({
        mutationFn: async (markerId: number) => {
            return studioAIService.deleteMessageMarker(markerId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['message-markers'] });
            onSuccess?.();
        },
        onError: (error) => {
            tracking.errorWithSource(error as Error, 'useMessageMarkerMutations.deleteMarker');
        },
    });

    const createMarker = useCallback(
        (status: MessageMarkerStatus, notes?: string) => {
            return createMarkerMutation.mutateAsync({ status, notes });
        },
        [createMarkerMutation],
    );

    const updateMarker = useCallback(
        (markerId: number, status?: MessageMarkerStatus, notes?: string) => {
            return updateMarkerMutation.mutateAsync({ markerId, status, notes });
        },
        [updateMarkerMutation],
    );

    const deleteMarker = useCallback(
        (markerId: number) => {
            return deleteMarkerMutation.mutateAsync(markerId);
        },
        [deleteMarkerMutation],
    );

    return {
        createMarker,
        updateMarker,
        deleteMarker,
        isLoading:
            createMarkerMutation.isPending ||
            updateMarkerMutation.isPending ||
            deleteMarkerMutation.isPending,
    };
}
